/**
 * Centralized URL Configuration
 * 
 * This file contains all external URLs used throughout the application.
 * Centralizing URLs improves maintainability and security by providing
 * a single source of truth for external service endpoints.
 */

// Social Media Platform URLs
export const SOCIAL_MEDIA_URLS = {
  DEVELOPERS: {
    FACEBOOK: 'https://developers.facebook.com',
    TWITTER: 'https://developer.twitter.com', 
    LINKEDIN: 'https://developer.linkedin.com',
  },
  PLATFORMS: {
    TWITTER: 'https://twitter.com',
    X_PLATFORM: 'https://x.com',
    FACEBOOK: 'http://www.facebook.com',
  },
} as const;

// Social Media API and Widget URLs
export const SOCIAL_MEDIA = {
  TWITTER: {
    WIDGETS_SCRIPT: 'https://platform.twitter.com/widgets.js',
  },
  LINKEDIN: {
    SHARE_URL: 'https://www.linkedin.com/sharing/share-offsite/',
  },
} as const;

// Documentation and Tool URLs
export const DOCUMENTATION_URLS = {
  PLAYWRIGHT: 'https://playwright.dev',
  SHADCN_UI: 'https://ui.shadcn.com',
} as const;

// Third-Party Service URLs
export const SERVICE_URLS = {
  AVATAR_PLACEHOLDER: 'https://i.pravatar.cc',
} as const;

// Application URLs (for development/testing)
export const APP_URLS = {
  PRODUCTION: 'https://ai-kurzy.online',
  BLOG: 'https://blog.ai-kurzy.online', 
  FUNCTIONS: 'https://europe-west1-ai-kurzy-sk.cloudfunctions.net',
} as const;

// Development URLs (for testing)
export const DEVELOPMENT_URLS = {
  SAMPLE_TWEET: 'https://twitter.com/elonmusk/status/1234567890',
  SAMPLE_X_TWEET: 'https://x.com/elonmusk/status/1234567890',
} as const;

// Helper function to get URL with fallback
export function getUrl(category: keyof typeof SOCIAL_MEDIA_URLS | keyof typeof DOCUMENTATION_URLS | keyof typeof SERVICE_URLS, key: string, fallback?: string): string {
  try {
    const urlCategory = {
      ...SOCIAL_MEDIA_URLS,
      ...DOCUMENTATION_URLS, 
      ...SERVICE_URLS,
    }[category] as Record<string, string>;
    
    return urlCategory?.[key] || fallback || '';
  } catch {
    return fallback || '';
  }
}

// Legacy exports for backward compatibility
export const EXTERNAL_URLS = {
  ...SOCIAL_MEDIA_URLS,
  ...DOCUMENTATION_URLS,
  ...SERVICE_URLS,
  ...APP_URLS,
  SOCIAL_MEDIA,
  DEVELOPMENT: DEVELOPMENT_URLS,
} as const;

export const urlHelpers = {
  getUrl,
  getSocialMediaUrl: (key: string, fallback?: string) => SOCIAL_MEDIA_URLS.PLATFORMS?.[key as keyof typeof SOCIAL_MEDIA_URLS.PLATFORMS] || fallback || '',
  getDocumentationUrl: (key: string, fallback?: string) => DOCUMENTATION_URLS?.[key as keyof typeof DOCUMENTATION_URLS] || fallback || '',
  getServiceUrl: (key: string, fallback?: string) => SERVICE_URLS?.[key as keyof typeof SERVICE_URLS] || fallback || '',
  getAppUrl: (key: string, fallback?: string) => APP_URLS?.[key as keyof typeof APP_URLS] || fallback || '',
  facebookShare: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  twitterShare: (url: string, text?: string) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}${text ? `&text=${encodeURIComponent(text)}` : ''}`,
  linkedInShare: (url: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
} as const;

// Type exports for better TypeScript support
export type SocialMediaUrl = typeof SOCIAL_MEDIA_URLS;
export type DocumentationUrl = typeof DOCUMENTATION_URLS;
export type ServiceUrl = typeof SERVICE_URLS;
export type AppUrl = typeof APP_URLS;